import { useState } from 'react';
import { Link } from 'react-router-dom';
import { line, Color, Size, pageKindLineText, t, type OrgExceptionRow } from '@bms2/shared';
import { Button, DatePicker, Select, TextInput } from '../../components/controls';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { Modal } from '../../components/modals/Modal';
import { PageModule } from '../../shell/PageModule';
import { useDocumentToday } from '../../shell/ViewerZone';
import { post, useApi } from '../records/api';
import { ORG_CHART_PATH, ORG_CHART_ROUTE, ORG_EXCEPTIONS_PATH, ORG_MOVE_PATH, type OrgChartAnswer } from './OrgChartPage';
import styles from './OrgChartPage.module.css';
import { useNotice } from '../../components/controls/useNotice';

/**
 * The reporting-line exceptions queue at `/org/exceptions` (piece 10, decision
 * 0079; piece 12's `queue:reporting-line-exceptions`). Each row names the
 * person and what broke, and carries the one action that resolves it: a move
 * that puts them under a manager who reaches the top.
 */
interface ExceptionsAnswer {
  canEdit: boolean;
  rows: OrgExceptionRow[];
}

interface Draft {
  employmentId: string;
  personName: string;
  bossEmploymentId: string;
  departmentId: string;
  effectiveOn: string;
  reason: string;
}

export function OrgExceptionsPage({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  // 🔨 Today is the viewer's own day, read from the shell rather than from the machine's clock (decision 0261).
  const today = useDocumentToday();
  const fetched = useApi<ExceptionsAnswer>(ORG_EXCEPTIONS_PATH, apiBaseUrl);
  const chart = useApi<OrgChartAnswer>(ORG_CHART_PATH, apiBaseUrl);
  const [noticeBanner, setNotice] = useNotice();
  const [draft, setDraft] = useState<Draft | null>(null);
  const cast = chart.state.kind === 'ready' ? [...chart.state.data.nodes, ...chart.state.data.unplaced] : [];
  const departments = chart.state.kind === 'ready' ? chart.state.data.departments : [];

  const save = async () => {
    if (!draft) return;
    const sent = await post(ORG_MOVE_PATH, {
      employmentId: draft.employmentId,
      bossEmploymentId: draft.bossEmploymentId || null,
      departmentId: draft.departmentId || null,
      effectiveOn: draft.effectiveOn,
      reason: draft.reason.trim(),
    }, apiBaseUrl);
    setNotice(sent.ok ? { tone: 'good', text: `${t('org.moveSaved')} · ${draft.personName}` } : { tone: 'error', text: sent.message });
    if (sent.ok) {
      setDraft(null);
      fetched.reload();
      chart.reload();
    }
  };

  return (
    <PageModule title={t('org.exceptionsTitle')} subtitle={pageKindLineText({ page: 'named', route: '/org/exceptions' })}>
      <div className={styles.page} data-page="org-exceptions">
        {noticeBanner}
        <p className={styles.meta}>
          <Link to={ORG_CHART_ROUTE}>{t('org.openChart')}</Link>
        </p>
        <AsyncStatePanel state={fetched.state} emptyAction={t('org.exceptionsEmpty')} onRetry={fetched.reload}>
          {(answer) => (
            <ul className={styles.list} data-org-exception-count={answer.rows.length}>
              {answer.rows.length === 0 ? <li className={styles.meta}>{t('org.exceptionsEmpty')}</li> : null}
              {answer.rows.map((row) => (
                <li key={`${row.employmentId}:${row.kind}`} className={styles.item} data-org-exception={row.employmentId} data-org-exception-kind={row.kind}>
                  <span className={styles.itemMain}>
                    <span className={styles.itemName}>{row.personName}</span>
                    <span className={styles.meta}>{line(row.title, row.departmentName ? `· ${row.departmentName}` : null)}</span>
                    <span className={styles.meta} data-org-exception-what="true">{row.what}</span>
                  </span>
                  {answer.canEdit ? (
                    <Button
                      size={Size.Small}
                      color={Color.Navy}
                      text={[t('org.movePerson')]}
                      actions={[() => setDraft({ employmentId: row.employmentId, personName: row.personName, bossEmploymentId: '', departmentId: '', effectiveOn: today, reason: '' })]}
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </AsyncStatePanel>

        {draft ? (
          <Modal
            key={draft.employmentId}
            open
            title={t('org.moveTitle')}
            subtitle={draft.personName}
            dataModal="org-exception-move"
            onCancel={() => setDraft(null)}
            actions={[
              { kind: 'primary', label: t('modal.save'), onAct: () => void save(), disabled: draft.reason.trim() === '' },
              { kind: 'cancel', label: t('modal.cancel'), onAct: () => setDraft(null) },
            ]}
          >
            <div className={styles.fields}>
              <Select size={Size.Medium}
                label={t('org.newManager')}
                value={draft.bossEmploymentId}
                onChange={(value) => setDraft({ ...draft, bossEmploymentId: value })}
                options={[{ value: '', label: t('org.noManager') }, ...cast.filter((node) => node.employmentId !== draft.employmentId).map((node) => ({ value: node.employmentId, label: line(node.label, node.title ? `— ${node.title}` : null) }))]}
                dataAttribute={{ 'data-org-move-boss': 'true' }}
              />
              <DatePicker size={Size.Small} label={t('org.effectiveDate')} value={draft.effectiveOn} onChange={(value) => setDraft({ ...draft, effectiveOn: value })} max={today} required name="orgExceptionEffectiveOn" />
              <Select size={Size.Medium} label={t('org.department')} value={draft.departmentId} onChange={(value) => setDraft({ ...draft, departmentId: value })} options={departments.map((row) => ({ value: row.id, label: row.name }))} placeholder={t('org.noManager')} />
              <TextInput size={Size.Large} label={t('org.reason')} value={draft.reason} onChange={(value) => setDraft({ ...draft, reason: value })} required />
            </div>
          </Modal>
        ) : null}
      </div>
    </PageModule>
  );
}
